import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import {
  formatAppCode,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import UsageReportBarList from "@/v2-backoffice-client/usage/report/UsageReportBarList"
import { formatLicenseDeniedReason } from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import {
  formatLicenseDeniedFeature,
  type UsageLicenseDeniedFeatureRow,
} from "@/v2-backoffice-client/usage/denied/usage.denied.utils"

export type UsageLicenseDeniedFeatureListProps = Omit<
  ComponentProps<"div">,
  "children"
> & {
  /** Features, most 28-day attempts first. */
  features: UsageLicenseDeniedFeatureRow[]
  limit: number
  /** Append the distinct people (and customers, when above one) behind each feature. */
  showPeople?: boolean
  /** Show how many customers asked for each feature (the portfolio's demand ranking). */
  showCustomers?: boolean
  emptyText: string
}

// The locked features a scope's people tried to use, ranked by attempts:
// the catalog name with the product and code under it, an amber bar for
// the feature's share of the top one, the attempts, who, and when last.
// One mapping onto the report bar list, so a customer's few features and
// the portfolio's demand ranking read the same way.
function UsageLicenseDeniedFeatureList(
  props: UsageLicenseDeniedFeatureListProps,
) {
  const {
    features,
    limit,
    showPeople = false,
    showCustomers = false,
    emptyText,
    ...divProps
  } = props

  const rows = features
    .filter((feature) => feature.Attempts28d > 0)
    .map((feature) => ({
      key: `${feature.AppCode}:${feature.FeatureCode}`,
      label: formatLicenseDeniedFeature(
        feature.FeatureCode,
        feature.FeatureName,
      ),
      sublabel: (
        <span className="flex items-center gap-1">
          <span
            aria-label={formatAppCode(feature.AppCode)}
            className="inline-block size-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: getAppCodeColor(feature.AppCode) }}
          />
          {feature.FeatureCode}
        </span>
      ),
      value: feature.Attempts28d,
      extra: (
        <>
          {showPeople && (
            <>
              {feature.Users28d} {feature.Users28d === 1 ? "person" : "people"}
              {showCustomers && feature.Customers28d > 0 && (
                <> · {feature.Customers28d} cust.</>
              )}
              {" · "}
            </>
          )}
          {feature.LastAt ? <RelativeDate date={feature.LastAt} /> : "—"}
        </>
      ),
      title: `${formatLicenseDeniedFeature(feature.FeatureCode, feature.FeatureName)}: ${formatNumber(feature.Attempts28d)} refused attempt${feature.Attempts28d === 1 ? "" : "s"} in 28 d${
        feature.AttemptsPrev28d > 0
          ? ` (${formatNumber(feature.AttemptsPrev28d)} the 28 before)`
          : ""
      } · ${formatLicenseDeniedReason(feature.ReasonCode)}`,
    }))

  return (
    <UsageReportBarList
      rows={rows}
      limit={limit}
      tone="warning"
      labelWidth="w-52"
      emptyText={emptyText}
      {...divProps}
    />
  )
}

export default UsageLicenseDeniedFeatureList
